import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import ExcelJS from "exceljs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { UploadCloud, Download, X, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const TEMPLATE_FILENAME = "milestone_import_template";
// Column order (drives BOTH the downloaded template and the parsed uploads)
const TEMPLATE_COLUMNS = [
  { key: "milestone", label: "Milestone", width: 32 },
  { key: "title", label: "Title", width: 28 },
  { key: "description", label: "Description", width: 48 },
  { key: "item_code", label: "Item #", width: 12 },
  { key: "qty", label: "Qty", width: 8 },
  { key: "unit", label: "Unit", width: 12 },
  { key: "unit_price", label: "Unit Price", width: 14 },
];

// Two reference examples shown in the dialog AND written into the downloaded template.
const EXAMPLE_ROWS = [
  {
    milestone: "Preliminary Works",
    title: "Floor protection",
    description: "Supply labour and material to lay floor protection across the unit",
    item_code: "1.1",
    qty: 1,
    unit: "Lot",
    unit_price: 550,
  },
  {
    milestone: "Carpentry Works",
    title: "Full-height shoe cabinet",
    description: "Custom shoe cabinet (L1200 x D400 x H2400) in laminate finish",
    item_code: "2.1",
    qty: 4,
    unit: "Foot/Run",
    unit_price: 400,
  },
];

const normKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const KEY_ALIASES = {
  milestone: ["milestone", "category", "section", "milestonename", "group"],
  title: ["title", "taskname", "name"],
  description: ["description", "desc", "details", "line", "work"],
  item_code: ["item", "itemcode", "itemno", "code", "no", "sn", "number"],
  qty: ["qty", "quantity", "quantum"],
  unit: ["unit", "uom", "units"],
  unit_price: ["unitprice", "price", "cost", "rate", "unitcost"],
};

function mapHeaders(headers) {
  const idx = {};
  headers.forEach((h, i) => {
    const k = normKey(h);
    for (const [target, aliases] of Object.entries(KEY_ALIASES)) {
      if (aliases.includes(k)) {
        if (!(target in idx)) idx[target] = i;
        break;
      }
    }
  });
  return idx;
}

function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(/[$,]/g, "");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Duplicate signature: milestone + (item_code || title). Case-insensitive.
function taskSignature(milestoneName, itemCode, title) {
  const ms = (milestoneName || "").trim().toLowerCase();
  const code = (itemCode || "").trim().toLowerCase();
  const t = (title || "").trim().toLowerCase();
  return code ? `${ms}::code::${code}` : `${ms}::title::${t}`;
}

/** Build XLSX template with a data-validation dropdown on the Milestone column. */
async function downloadTemplate(firmLibrary) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "OrkaiFlow";
  wb.created = new Date();

  // Main sheet
  const ws = wb.addWorksheet("Tasks", { views: [{ state: "frozen", ySplit: 1 }] });
  ws.columns = TEMPLATE_COLUMNS.map((c) => ({ header: c.label, key: c.key, width: c.width }));

  // Header styling
  const header = ws.getRow(1);
  header.font = { bold: true, color: { argb: "FF2C2A29" } };
  header.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF7F4EC" },
  };
  header.alignment = { vertical: "middle" };
  header.height = 22;

  // No example rows written into the template — examples live inside the dialog
  // so the user opens a clean sheet and can start filling from row 2.

  // Milestone dropdown validation (rows 2..1000)
  const milestones = (firmLibrary || []).filter(Boolean);
  if (milestones.length > 0) {
    // Excel requires list validation as a comma-separated string wrapped in quotes.
    // If total length exceeds 255 chars, put the list on a hidden sheet and reference by range.
    const joined = milestones.join(",");
    let formulae;
    if (joined.length <= 250) {
      formulae = [`"${joined.replace(/"/g, '""')}"`];
    } else {
      const listSheet = wb.addWorksheet("_milestones", { state: "hidden" });
      milestones.forEach((name, i) => {
        listSheet.getCell(`A${i + 1}`).value = name;
      });
      formulae = [`_milestones!$A$1:$A$${milestones.length}`];
    }
    for (let r = 2; r <= 1000; r++) {
      ws.getCell(`A${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae,
        showErrorMessage: true,
        errorTitle: "Invalid milestone",
        error: "Pick a milestone from the dropdown. It must match your firm's master library.",
      };
    }
  }

  // Info sheet
  const info = wb.addWorksheet("Read me");
  info.getColumn(1).width = 100;
  const infoLines = [
    "Milestone import template",
    "",
    "Fill one row per task starting from row 2. The Milestone column is a dropdown that matches your Firm Settings → Milestone Library.",
    "",
    "Columns:",
    "  • Milestone — pick from the dropdown (matches your Firm Settings → Milestone Library).",
    "  • Title — short task name shown on the board.",
    "  • Description — optional longer detail.",
    "  • Item # — quotation line reference (e.g. 1.1, 2.3).",
    "  • Qty — numeric quantity.",
    "  • Unit — Lot, nos, Foot/Run, etc.",
    "  • Unit Price — numeric unit cost. Line total is auto-calculated.",
    "",
    "Duplicates: rows that match an existing task (by Milestone + Item # or Title) will be flagged and skipped on import.",
  ];
  infoLines.forEach((t, i) => {
    const cell = info.getCell(i + 1, 1);
    cell.value = t;
    if (i === 0) cell.font = { bold: true, size: 14 };
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${TEMPLATE_FILENAME}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Parse either an .xlsx or .csv file into a { headers, rows } shape. */
async function parseUpload(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx") || name.endsWith(".xlsm")) {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.getWorksheet("Tasks") || wb.worksheets[0];
    if (!ws) return { headers: [], rows: [] };
    const rows = [];
    ws.eachRow({ includeEmpty: false }, (row) => {
      const arr = [];
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        arr[colNumber - 1] = cell.value != null ? String(cell.value) : "";
      });
      rows.push(arr);
    });
    if (!rows.length) return { headers: [], rows: [] };
    return { headers: rows[0].map((h) => String(h || "").trim()), rows: rows.slice(1) };
  }
  // CSV fallback
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        if (!data.length) return resolve({ headers: [], rows: [] });
        resolve({
          headers: data[0].map((h) => String(h).trim()),
          rows: data.slice(1),
        });
      },
      error: (err) => reject(err),
    });
  });
}

export default function TaskImportDialog({
  open,
  onOpenChange,
  projectId,
  onDone,
  firmMilestoneLibrary = [],
  existingTasks = [],
}) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [downloading, setDownloading] = useState(false);
  // Firm library can be passed in OR pulled here as a fallback
  const [libraryFallback, setLibraryFallback] = useState([]);

  const library = firmMilestoneLibrary.length ? firmMilestoneLibrary : libraryFallback;
  const librarySet = useMemo(
    () => new Set((library || []).map((n) => n.trim().toLowerCase())),
    [library]
  );

  // Existing task signatures used to detect duplicates
  const existingSignatures = useMemo(() => {
    const set = new Set();
    const idToName = new Map();
    // We don't have milestone name → id map here; caller passes existingTasks with milestone_name already resolved.
    (existingTasks || []).forEach((t) => {
      const ms = t.milestone_name || "";
      set.add(taskSignature(ms, t.item_code, t.title));
    });
    return set;
  }, [existingTasks]);

  useEffect(() => {
    if (!open) return;
    // Refresh firm library if not provided
    if (firmMilestoneLibrary.length) return;
    api
      .get("/firm/settings")
      .then(({ data }) => setLibraryFallback(data?.firm?.milestone_templates || []))
      .catch(() => setLibraryFallback([]));
  }, [open, firmMilestoneLibrary.length]);

  const reset = () => {
    setRows([]);
    setFileName("");
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) reset();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const { headers, rows: raw } = await parseUpload(file);
      if (!headers.length) {
        toast.error("The file is empty.");
        return;
      }
      const idx = mapHeaders(headers);
      if (idx.title === undefined && idx.description === undefined) {
        toast.error("Missing a 'Title' or 'Description' column.");
        return;
      }
      const parsed = [];
      for (let i = 0; i < raw.length; i++) {
        const row = raw[i];
        const milestone = idx.milestone !== undefined ? String(row[idx.milestone] ?? "").trim() : "";
        const title = idx.title !== undefined ? String(row[idx.title] ?? "").trim() : "";
        const description = idx.description !== undefined ? String(row[idx.description] ?? "").trim() : "";
        // Empty row guard
        if (!milestone && !title && !description) continue;

        parsed.push({
          id: `r-${i}`,
          milestone,
          title: title || (description ? description.slice(0, 80) : ""),
          description,
          item_code: idx.item_code !== undefined ? String(row[idx.item_code] ?? "").trim() : "",
          qty: parseNumber(row[idx.qty]),
          unit: idx.unit !== undefined ? String(row[idx.unit] ?? "").trim() : "",
          unit_price: parseNumber(row[idx.unit_price]),
          include: true,
        });
      }
      if (!parsed.length) {
        toast.error("No valid rows found.");
        return;
      }
      setRows(parsed);
      toast.success(`Parsed ${parsed.length} row${parsed.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(`Could not read file: ${err.message}`);
    }
  };

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  /** Row validation — status is one of: new | duplicate | error */
  const rowsWithStatus = useMemo(() => {
    return rows.map((r) => {
      const problems = [];
      if (!r.milestone) problems.push("Milestone is required");
      else if (librarySet.size > 0 && !librarySet.has(r.milestone.trim().toLowerCase())) {
        problems.push("Milestone is not in the firm library");
      }
      if (!r.title && !r.description) problems.push("Title or Description required");
      // Qty is required — mirrors the backend rule so the error surfaces
      // per-row instead of hitting the API 400 wall.
      if (r.qty === null || r.qty === undefined || r.qty === "") {
        problems.push("Quantity is required");
      } else if (typeof r.qty === "number" && (Number.isNaN(r.qty) || r.qty < 0)) {
        problems.push("Quantity must be a positive number");
      }
      const sig = taskSignature(r.milestone, r.item_code, r.title || r.description);
      const isDuplicate = existingSignatures.has(sig);
      let status = "new";
      if (problems.length > 0) status = "error";
      else if (isDuplicate) status = "duplicate";
      return { ...r, __status: status, __problems: problems };
    });
  }, [rows, librarySet, existingSignatures]);

  const stats = useMemo(() => {
    const selected = rowsWithStatus.filter((r) => r.include);
    const hasBlockingErrors = selected.some((r) => r.__status === "error");
    const newRows = selected.filter((r) => r.__status === "new");
    const dupRows = selected.filter((r) => r.__status === "duplicate");
    const errRows = selected.filter((r) => r.__status === "error");
    const bySection = new Map();
    let total = 0;
    for (const r of newRows) {
      const key = r.milestone.trim() || "Uncategorised";
      bySection.set(key, (bySection.get(key) || 0) + 1);
      if (r.qty != null && r.unit_price != null) total += r.qty * r.unit_price;
    }
    return {
      total,
      hasBlockingErrors,
      selected: selected.length,
      newCount: newRows.length,
      dupCount: dupRows.length,
      errCount: errRows.length,
      sectionList: [...bySection.entries()],
    };
  }, [rowsWithStatus]);

  const commit = async () => {
    if (stats.hasBlockingErrors) {
      toast.error("Fix the highlighted errors before importing.");
      return;
    }
    // Only send "new" rows — duplicates are skipped even if ticked.
    const toImport = rowsWithStatus.filter((r) => r.include && r.__status === "new");
    if (!toImport.length) {
      toast.error("Nothing new to import.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        rows: toImport.map((r) => ({
          milestone_name: r.milestone.trim(),
          item_code: r.item_code || null,
          description: r.description.trim() || r.title.trim(),
          title: r.title.trim() || null,
          qty: r.qty,
          unit: r.unit || null,
          unit_price: r.unit_price,
        })),
        auto_create_milestones: true,
      };
      const { data } = await api.post(`/projects/${projectId}/tasks/import`, payload);
      toast.success(
        `Imported ${data.created_tasks} task${data.created_tasks === 1 ? "" : "s"} · ${data.created_milestones} new milestone${data.created_milestones === 1 ? "" : "s"}`
      );
      onDone && onDone();
      handleClose(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      // Refresh library right before generating template (per user requirement #3)
      let latest = library;
      try {
        const { data } = await api.get("/firm/settings");
        latest = data?.firm?.milestone_templates || library;
        setLibraryFallback(latest);
      } catch (_) {
        // Use whatever we have
      }
      if (!latest || latest.length === 0) {
        toast.warning(
          "Your firm milestone library is empty. Add milestones in Firm Settings first, then download the template."
        );
        return;
      }
      await downloadTemplate(latest);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(`Could not build template: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const statusBadge = (status) => {
    if (status === "error") {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEE2E2] text-[#9F1239] uppercase tracking-wider">Error</span>;
    }
    if (status === "duplicate") {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEF3C7] text-[#92400E] uppercase tracking-wider">Duplicate</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DCFCE7] text-[#166534] uppercase tracking-wider">New</span>;
  };

  const canCommit = !busy && !stats.hasBlockingErrors && stats.newCount > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-5xl w-[95vw] rounded-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="task-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import milestones &amp; tasks</DialogTitle>
          <DialogDescription>
            Use the <span className="font-semibold text-[#2C2A29]">milestone_import_template</span> to bring milestones and their tasks into this project.
            Fill in the required details so your Project Hub is prepopulated and you can plan, assign and manage the work straight away.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1 -mr-1">
          {rows.length === 0 && (
            <div className="space-y-4">
              {/* Two examples for reference */}
              <div className="card-soft !bg-[#F7F4EC]">
                <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">
                  Two examples for reference
                </div>
                <div className="overflow-x-auto rounded-xl border border-[#EBE5D9] bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FBF8F1]">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-[#7A7571]">
                        {TEMPLATE_COLUMNS.map((c) => (
                          <th key={c.key} className="p-2 whitespace-nowrap">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {EXAMPLE_ROWS.map((r, i) => (
                        <tr key={i} className="border-t border-[#EBE5D9]">
                          {TEMPLATE_COLUMNS.map((c) => (
                            <td key={c.key} className="p-2 align-top">{String(r[c.key] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border-2 border-dashed border-[#EBE5D9] p-8 text-center">
                <UploadCloud size={40} className="mx-auto text-[#7A7571] mb-3" />
                <p className="text-sm text-[#7A7571] mb-4">
                  Drag &amp; drop your completed template here, or pick a file.
                </p>
                <label className="btn-primary inline-flex items-center gap-2 cursor-pointer">
                  <UploadCloud size={16} /> Choose file (.xlsx or .csv)
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    data-testid="task-import-file-input"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                </label>
              </div>

              <div className="card-soft !bg-[#F7F4EC]">
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-[#7A7571] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#7A7571] flex-1">
                    <div className="text-[#2C2A29] font-semibold mb-1">How the template works</div>
                    <div className="mb-1">
                      Columns: <code className="text-xs">Milestone, Title, Description, Item #, Qty, Unit, Unit Price</code>
                    </div>
                    <div className="text-xs">
                      The Milestone column is a dropdown, prefilled with your Firm Settings → Milestone Library
                      {library.length > 0 ? ` (${library.length} available)` : ""}.
                      Existing tasks in this project will be detected and flagged as duplicates when you upload.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onDownload}
                    disabled={downloading}
                    data-testid="task-import-template-btn"
                    className="btn-secondary text-sm inline-flex items-center gap-2 flex-shrink-0"
                  >
                    {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    Download template
                  </button>
                </div>
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[#F7F4EC] rounded-2xl p-4">
                <div className="text-sm text-[#7A7571]" data-testid="import-summary">
                  <span className="text-[#2C2A29] font-semibold">{fileName}</span> ·{" "}
                  <span className="text-[#166534] font-semibold" data-testid="import-summary-new">{stats.newCount} new</span> ·{" "}
                  <span className="text-[#92400E] font-semibold" data-testid="import-summary-dup">{stats.dupCount} duplicate</span> ·{" "}
                  <span className="text-[#9F1239] font-semibold" data-testid="import-summary-err">{stats.errCount} error{stats.errCount === 1 ? "" : "s"}</span>
                  {stats.total > 0 && (
                    <>
                      {" · "}${stats.total.toLocaleString(undefined, { maximumFractionDigits: 2 })} value
                    </>
                  )}
                </div>
                <button
                  onClick={reset}
                  data-testid="task-import-reset"
                  className="btn-ghost text-sm inline-flex items-center gap-1"
                >
                  <X size={14} /> Reset
                </button>
              </div>

              {stats.hasBlockingErrors && (
                <div className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-3 text-sm text-[#9F1239] flex items-start gap-2" data-testid="import-error-banner">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">
                      {stats.errCount} row{stats.errCount === 1 ? "" : "s"} need fixing before you can import.
                    </div>
                    <div className="text-xs mt-0.5">
                      Each row below shows what&apos;s missing. Fix them in-place (Milestone, Title/Description and Quantity are all required) and the errors will clear as you type.
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-[#EBE5D9] overflow-hidden">
                <div className="max-h-[45vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F4EC] sticky top-0 z-10">
                      <tr className="text-left text-xs uppercase tracking-widest text-[#7A7571]">
                        <th className="p-3 w-10"></th>
                        <th className="p-3 w-24">Status</th>
                        <th className="p-3">Milestone</th>
                        <th className="p-3">Title</th>
                        <th className="p-3 w-20">Item #</th>
                        <th className="p-3 w-16">Qty</th>
                        <th className="p-3 w-20">Unit</th>
                        <th className="p-3 w-24">Unit Price</th>
                        <th className="p-3 w-24 text-right">Line total</th>
                        <th className="p-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowsWithStatus.map((r) => {
                        const lineTotal =
                          r.qty != null && r.unit_price != null ? r.qty * r.unit_price : null;
                        const rowTint =
                          r.__status === "error"
                            ? "bg-[#FEF2F2]"
                            : r.__status === "duplicate"
                            ? "bg-[#FFFBEB]"
                            : "";
                        return (
                          <tr
                            key={r.id}
                            className={`border-t border-[#EBE5D9] ${rowTint} ${r.include ? "" : "opacity-40"}`}
                            data-testid={`import-row-${r.id}`}
                            title={r.__problems.join(", ")}
                          >
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={r.include}
                                disabled={r.__status === "duplicate"}
                                onChange={(e) => updateRow(r.id, { include: e.target.checked })}
                              />
                            </td>
                            <td className="p-2" data-testid={`import-row-${r.id}-status`}>
                              {statusBadge(r.__status)}
                              {r.__problems.length > 0 && (
                                <ul className="mt-1 space-y-0.5" data-testid={`import-row-${r.id}-problems`}>
                                  {r.__problems.map((p, idx) => (
                                    <li key={idx} className="text-[10px] text-[#9F1239] leading-tight flex items-start gap-1">
                                      <AlertCircle size={9} className="mt-0.5 flex-shrink-0" /> {p}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            <td className="p-2">
                              <select
                                className={`input-soft !py-1.5 !px-2 text-sm ${r.__problems.some((p) => p.toLowerCase().includes("milestone")) ? "!border-[#F87171]" : ""}`}
                                value={library.some((m) => m.toLowerCase() === r.milestone.toLowerCase()) ? r.milestone : ""}
                                onChange={(e) => updateRow(r.id, { milestone: e.target.value })}
                                data-testid={`import-row-${r.id}-milestone`}
                              >
                                <option value="">— Pick milestone —</option>
                                {library.map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                                {r.milestone && !library.some((m) => m.toLowerCase() === r.milestone.toLowerCase()) && (
                                  <option value={r.milestone} disabled>
                                    {r.milestone} (not in library)
                                  </option>
                                )}
                              </select>
                            </td>
                            <td className="p-2">
                              <input
                                className="input-soft !py-1.5 !px-2 text-sm"
                                value={r.title}
                                onChange={(e) => updateRow(r.id, { title: e.target.value })}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                className="input-soft !py-1.5 !px-2 text-sm"
                                value={r.item_code}
                                onChange={(e) => updateRow(r.id, { item_code: e.target.value })}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                step="any"
                                data-testid={`import-row-${r.id}-qty`}
                                className={`input-soft !py-1.5 !px-2 text-sm ${r.__problems.some((p) => p.toLowerCase().includes("quantity")) ? "!border-[#F87171]" : ""}`}
                                value={r.qty ?? ""}
                                onChange={(e) =>
                                  updateRow(r.id, {
                                    qty: e.target.value === "" ? null : Number(e.target.value),
                                  })
                                }
                              />
                            </td>
                            <td className="p-2">
                              <input
                                className="input-soft !py-1.5 !px-2 text-sm"
                                value={r.unit}
                                onChange={(e) => updateRow(r.id, { unit: e.target.value })}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                step="any"
                                className="input-soft !py-1.5 !px-2 text-sm"
                                value={r.unit_price ?? ""}
                                onChange={(e) =>
                                  updateRow(r.id, {
                                    unit_price:
                                      e.target.value === "" ? null : Number(e.target.value),
                                  })
                                }
                              />
                            </td>
                            <td className="p-2 text-right font-mono text-sm text-[#2C2A29]">
                              {lineTotal != null
                                ? `$${lineTotal.toLocaleString(undefined, {
                                    maximumFractionDigits: 2,
                                  })}`
                                : "—"}
                            </td>
                            <td className="p-2 text-center">
                              <button
                                onClick={() => removeRow(r.id)}
                                className="btn-ghost !p-1.5 text-[#9F1239]"
                                title="Remove row"
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {stats.sectionList.length > 0 && (
                <div className="card-soft !bg-[#F7F4EC] !py-3">
                  <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">
                    New tasks per milestone
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stats.sectionList.map(([name, n]) => (
                      <span
                        key={name}
                        className="px-3 py-1 rounded-full bg-white border border-[#EBE5D9] text-xs text-[#2C2A29]"
                      >
                        {name} · {n}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#EBE5D9] mt-2">
            <button onClick={() => handleClose(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={commit}
              disabled={!canCommit}
              data-testid="task-import-commit"
              className={`btn-primary inline-flex items-center gap-2 ${!canCommit ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {busy
                ? "Importing…"
                : stats.hasBlockingErrors
                ? "Resolve errors to continue"
                : stats.newCount === 0
                ? "Nothing new to import"
                : `Create ${stats.newCount} task${stats.newCount === 1 ? "" : "s"}`}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
